import zod from 'zod';
export const NumberFormatSchema = zod.enum([
  'number',
  'numberWithCommas',
  'percent',
  'currencyYen',
  'currencyINR',
  'currencyCNY',
  'currencyUSD',
  'currencyEUR',
  'currencyGBP',
  'currencyRUB',
]);
export type NumberFormat = zod.infer<typeof NumberFormatSchema>;

const currency = (currency: string): Intl.NumberFormatOptions => ({
  style: 'currency',
  currency,
  // `narrowSymbol` renders `₽` instead of the ISO code (`RUB 800`) in
  // locales that have no own symbol for the currency.
  currencyDisplay: 'narrowSymbol',
});

// Currencies rendered in their home locale regardless of the browser locale,
// so rubles read naturally as `1 141 251 ₽` rather than `₽1,141,251`.
const currencyLocale: Partial<Record<NumberFormat, string>> = {
  currencyRUB: 'ru-RU',
};

const numberFormatDefaultConfig: Record<
  NumberFormat,
  Intl.NumberFormatOptions
> = {
  number: { style: 'decimal', useGrouping: false },
  numberWithCommas: { style: 'decimal', useGrouping: true },
  percent: { style: 'percent', useGrouping: false },
  currencyINR: currency('INR'),
  currencyYen: currency('JPY'),
  currencyCNY: currency('CNY'),
  currencyUSD: currency('USD'),
  currencyEUR: currency('EUR'),
  currencyGBP: currency('GBP'),
  currencyRUB: currency('RUB'),
};

/**
 * Format a numeric statistic result (sum/avg/min/max/median/range) with the
 * column's number format. Non-numeric results (e.g. 'None') pass through
 * unchanged. For formula columns this also applies the display-only
 * `prefix`/`suffix` affixes so totals match the cell rendering.
 */
export function formatStatsValue(
  rawValue: string,
  data: Record<string, unknown> | null | undefined
): string {
  if (typeof rawValue !== 'string') {
    return String(rawValue);
  }
  const num = Number(rawValue);
  if (rawValue === 'None' || !Number.isFinite(num)) {
    return rawValue;
  }
  const format = data?.format as NumberFormat | undefined;
  if (!format || !NumberFormatSchema.options.includes(format)) {
    return rawValue;
  }
  const decimals = typeof data?.decimal === 'number' ? data.decimal : undefined;
  const prefix = typeof data?.prefix === 'string' ? data.prefix : '';
  const suffix = typeof data?.suffix === 'string' ? data.suffix : '';
  return `${prefix}${formatNumber(num, format, decimals)}${suffix}`;
}

export function formatNumber(
  value: number,
  format: NumberFormat,
  decimals?: number
) {
  const formatterOptions = { ...numberFormatDefaultConfig[format] };
  if (decimals !== undefined) {
    // for feature flag should default to 0 after release
    Object.assign(formatterOptions, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  const formatter = new Intl.NumberFormat(
    getFormatLocale(format),
    formatterOptions
  );
  return formatter.format(value);
}

/** Locale used to render values of the given format. */
export function getFormatLocale(format: NumberFormat): string {
  return currencyLocale[format] ?? navigator.language;
}

export function getLocaleDecimalSeparator(locale?: string) {
  return (1.1).toLocaleString(locale ?? navigator.language).slice(1, 2);
}

// Since we Intl does not provide a parse function we just made it ourself
export function parseNumber(value: string, decimalSeparator?: string): number {
  decimalSeparator = decimalSeparator ?? getLocaleDecimalSeparator();

  // Normalize decimal separator to a period for consistency
  const normalizedValue = value.replace(
    new RegExp(`\\${decimalSeparator}`, 'g'),
    '.'
  );

  // Remove any leading and trailing non-numeric characters except valid signs, decimal points, and exponents
  let sanitizedValue = normalizedValue.replace(/^[^\d-+eE.]+|[^\d]+$/g, '');

  // Remove non-numeric characters except decimal points, exponents, and valid signs
  sanitizedValue = sanitizedValue.replace(/[^0-9.eE+-]/g, '');

  // Handle multiple signs: Keep only the first sign
  sanitizedValue = sanitizedValue.replace(/([-+]){2,}/g, '$1');

  // Handle misplaced signs: Keep only the leading sign and sign after 'e' or 'E'
  sanitizedValue = sanitizedValue.replace(
    /^([-+]?)[^eE]*([eE][-+]?\d+)?$/,
    (_, p1, p2) =>
      p1 +
      sanitizedValue.replace(/[eE].*/, '').replace(/[^\d.]/g, '') +
      (p2 || '')
  );

  // Handle multiple decimal points: Keep only the first one in the main part
  sanitizedValue = sanitizedValue.replace(/(\..*)\./g, '$1');

  // If there is an 'e' or 'E', handle the scientific notation
  if (/[eE]/.test(sanitizedValue)) {
    const [base, exp] = sanitizedValue.split(/[eE]/);
    if (
      !base ||
      !exp ||
      exp.includes('.') ||
      exp.includes('e') ||
      exp.includes('E')
    ) {
      return NaN; // Invalid scientific notation
    }
    return parseFloat(sanitizedValue);
  }

  return parseFloat(sanitizedValue);
}
